import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '../components/ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Plus, Trash2, MapPin, Loader2, Users, FileText, Phone, User, ChevronRight, Clock, FileUp, Filter as FilterIcon, X, Search, Check } from 'lucide-react';

const STATUS_COLORS = { levantada: '#3B82F6', en_proceso: '#F59E0B', turnada: '#8B5CF6', resuelta: '#10B981', cancelada: '#EF4444' };
const STATUS_LABELS = { levantada: 'Levantada', en_proceso: 'En Proceso', turnada: 'Turnada', resuelta: 'Resuelta', cancelada: 'Cancelada' };

export default function SeccionesPage() {
  const { api, user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [secciones, setSecciones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ numero: '', nombre: '', lat_center: '', lng_center: '', radio_km: '1.0' });
  const [countsBySeccion, setCountsBySeccion] = useState({});
  const [detail, setDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [uploadingDistritosKml, setUploadingDistritosKml] = useState(false);
  const [uploadingSeccionesKml, setUploadingSeccionesKml] = useState(false);
  const distritosKmlRef = useRef(null);
  const seccionesKmlRef = useRef(null);
  const [search, setSearch] = useState('');
  const [selectedFilters, setSelectedFilters] = useState(new Set());

  const loadSecciones = useCallback(async () => {
    try {
      const [sRes, statsRes] = await Promise.all([
        api.get('/secciones', { params: { limit: 2000 } }),
        api.get('/dashboard/stats'),
      ]);
      setSecciones(sRes.data?.items || sRes.data || []);
      const counts = {};
      (statsRes.data?.por_seccion || []).forEach(s => { counts[s.seccion] = s.count; });
      setCountsBySeccion(counts);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [api]);

  useEffect(() => { loadSecciones(); }, [loadSecciones]);

  const handleCreate = async () => {
    if (!form.numero || !form.nombre || !form.lat_center || !form.lng_center) { toast.error('Completa todos los campos'); return; }
    setCreating(true);
    try {
      await api.post('/secciones', { numero: form.numero, nombre: form.nombre, lat_center: parseFloat(form.lat_center), lng_center: parseFloat(form.lng_center), radio_km: parseFloat(form.radio_km) || 1.0 });
      toast.success('Sección creada');
      setForm({ numero: '', nombre: '', lat_center: '', lng_center: '', radio_km: '1.0' });
      setDialogOpen(false);
      loadSecciones();
    } catch (err) {
      console.error(err);
      toast.error('Error creando sección');
    }
    finally { setCreating(false); }
  };

  const handleDelete = async (id, e) => {
    e.stopPropagation();
    try {
      await api.delete(`/secciones/${id}`);
      toast.success('Eliminada');
      loadSecciones();
    } catch (err) {
      console.error(err);
      toast.error('Error');
    }
  };

  const openDetail = async (seccion) => {
    setLoadingDetail(true);
    setDetail({ numero: seccion.numero, nombre: seccion.nombre, gestiones: [], vecinos: [], total_gestiones: 0, total_vecinos: 0 });
    try {
      const res = await api.get(`/secciones/${seccion.numero}/detail`);
      setDetail({ ...res.data, nombre: seccion.nombre });
    } catch (err) {
      console.error(err);
      toast.error('Error al cargar el detalle');
    }
    finally { setLoadingDetail(false); }
  };

  const handleKmlUpload = async (file, kind) => {
    if (!file) return;
    const setLoad = kind === 'distritos' ? setUploadingDistritosKml : setUploadingSeccionesKml;
    const inputRef = kind === 'distritos' ? distritosKmlRef : seccionesKmlRef;
    setLoad(true);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const url = kind === 'distritos' ? '/distritos/kml' : '/secciones/kml';
      const res = await api.post(url, fd, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 180000 });
      toast.success(res.data.message || `KML importado`);
      loadSecciones();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error importando KML');
    } finally {
      setLoad(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  // Filtered + searched list (Excel-style)
  const filtered = useMemo(() => {
    let list = secciones;
    if (search) {
      const s = search.toLowerCase();
      list = list.filter(x =>
        x.numero?.toLowerCase().includes(s) ||
        x.nombre?.toLowerCase().includes(s) ||
        x.demarcacion?.toLowerCase().includes(s)
      );
    }
    if (selectedFilters.size > 0) {
      list = list.filter(x => selectedFilters.has(x.numero));
    }
    return list;
  }, [secciones, search, selectedFilters]);

  const toggleFilter = (numero) => {
    setSelectedFilters(prev => {
      const next = new Set(prev);
      if (next.has(numero)) next.delete(numero); else next.add(numero);
      return next;
    });
  };
  const clearFilters = () => setSelectedFilters(new Set());
  const selectAll = () => setSelectedFilters(new Set(secciones.map(s => s.numero)));

  return (
    <div className="space-y-6" data-testid="secciones-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-4xl sm:text-5xl font-black tracking-tighter text-[#0A0A0A]" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>Secciones Electorales</h1>
          <p className="text-base text-[#52525B] mt-1">Haz clic en una sección para ver sus gestiones y el directorio de vecinos</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {isAdmin && (
            <>
              <input
                ref={distritosKmlRef}
                type="file"
                accept=".kml"
                className="hidden"
                onChange={e => handleKmlUpload(e.target.files?.[0], 'distritos')}
                data-testid="kml-distritos-input"
              />
              <input
                ref={seccionesKmlRef}
                type="file"
                accept=".kml"
                className="hidden"
                onChange={e => handleKmlUpload(e.target.files?.[0], 'secciones')}
                data-testid="kml-secciones-input"
              />
              <Button
                data-testid="upload-kml-distritos-btn"
                variant="outline"
                onClick={() => distritosKmlRef.current?.click()}
                disabled={uploadingDistritosKml}
                className="h-12 px-5 rounded-md font-bold border-[#E4E4E7] hover:bg-[#0052FF] hover:text-white hover:border-[#0052FF]"
              >
                {uploadingDistritosKml ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileUp className="w-4 h-4 mr-2" />}
                Subir KML Distritos
              </Button>
              <Button
                data-testid="upload-kml-secciones-btn"
                variant="outline"
                onClick={() => seccionesKmlRef.current?.click()}
                disabled={uploadingSeccionesKml}
                className="h-12 px-5 rounded-md font-bold border-[#E4E4E7] hover:bg-[#10B981] hover:text-white hover:border-[#10B981]"
              >
                {uploadingSeccionesKml ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileUp className="w-4 h-4 mr-2" />}
                Subir KML Secciones
              </Button>
            </>
          )}
          {isAdmin && (
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-seccion-btn" className="bg-[#0052FF] hover:bg-[#0040CC] text-white h-12 px-6 rounded-md font-bold"><Plus className="w-4 h-4 mr-2" /> Nueva</Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md">
              <DialogHeader><DialogTitle className="font-bold text-[#0A0A0A]" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>Nueva Sección Electoral</DialogTitle></DialogHeader>
              <div className="space-y-4 mt-4">
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2"><Label className="text-xs uppercase font-bold text-[#52525B]">Número</Label><Input data-testid="seccion-numero-input" className="h-12 rounded-md" value={form.numero} onChange={e => setForm({ ...form, numero: e.target.value })} placeholder="0101" /></div>
                  <div className="space-y-2"><Label className="text-xs uppercase font-bold text-[#52525B]">Radio (km)</Label><Input data-testid="seccion-radio-input" type="number" className="h-12 rounded-md" value={form.radio_km} onChange={e => setForm({ ...form, radio_km: e.target.value })} step="0.1" /></div>
                </div>
                <div className="space-y-2"><Label className="text-xs uppercase font-bold text-[#52525B]">Nombre</Label><Input data-testid="seccion-nombre-input" className="h-12 rounded-md" value={form.nombre} onChange={e => setForm({ ...form, nombre: e.target.value })} placeholder="Centro Histórico" /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2"><Label className="text-xs uppercase font-bold text-[#52525B]">Latitud</Label><Input data-testid="seccion-lat-input" type="number" className="h-12 rounded-md" value={form.lat_center} onChange={e => setForm({ ...form, lat_center: e.target.value })} placeholder="19.4326" step="0.0001" /></div>
                  <div className="space-y-2"><Label className="text-xs uppercase font-bold text-[#52525B]">Longitud</Label><Input data-testid="seccion-lng-input" type="number" className="h-12 rounded-md" value={form.lng_center} onChange={e => setForm({ ...form, lng_center: e.target.value })} placeholder="-99.1332" step="0.0001" /></div>
                </div>
                <Button data-testid="create-seccion-btn" onClick={handleCreate} disabled={creating} className="w-full h-12 bg-[#0052FF] hover:bg-[#0040CC] text-white rounded-md font-bold">
                  {creating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />} Crear
                </Button>
              </div>
            </DialogContent>
          </Dialog>
          )}
        </div>
      </div>

      {/* Search + Excel-style Filter (visible to all roles) */}
      <div className="flex flex-wrap items-center gap-2 bg-white border border-[#E4E4E7] rounded-md p-2">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#52525B]" />
          <Input
            data-testid="secciones-search"
            className="h-10 pl-10 rounded-md border-0 focus-visible:ring-0 shadow-none"
            placeholder="Buscar por número, nombre o demarcación..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
        <Popover>
          <PopoverTrigger asChild>
            <Button
              data-testid="filter-btn"
              variant="outline"
              className={`h-10 rounded-md font-bold border-[#E4E4E7] hover:bg-[#10B981] hover:text-white hover:border-[#10B981] ${selectedFilters.size > 0 ? 'bg-[#10B981]/10 border-[#10B981] text-[#10B981]' : ''}`}
            >
              <FilterIcon className="w-4 h-4 mr-2" />
              Filtro ({selectedFilters.size}/{secciones.length})
            </Button>
          </PopoverTrigger>
          <PopoverContent align="end" className="w-72 p-0">
            <FilterPanel
              secciones={secciones}
              selected={selectedFilters}
              onToggle={toggleFilter}
              onSelectAll={selectAll}
              onClear={clearFilters}
            />
          </PopoverContent>
        </Popover>
        {selectedFilters.size > 0 && (
          <Button data-testid="clear-filter-btn" variant="ghost" size="sm" onClick={clearFilters} className="h-10 text-[#52525B]">
            <X className="w-4 h-4 mr-1" /> Limpiar
          </Button>
        )}
      </div>

      {isAdmin && (
        <Card className="bg-[#0052FF]/5 border border-[#0052FF]/20 rounded-md">
          <CardContent className="p-4"><p className="text-sm text-[#0A0A0A]"><span className="font-bold">Tip:</span> Sube el <span className="font-mono text-[#0052FF]">KML de distritos</span> y luego el <span className="font-mono text-[#10B981]">KML de secciones</span> del INE para poblar automáticamente los polígonos reales y datos de población.</p></CardContent>
        </Card>
      )}

      {loading ? (
        <div className="flex items-center justify-center h-40"><div className="w-8 h-8 border-2 border-[#0052FF] border-t-transparent rounded-full animate-spin" /></div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16">
          <MapPin className="w-12 h-12 text-[#E4E4E7] mx-auto mb-4" />
          <p className="text-lg font-semibold text-[#0A0A0A]">
            {secciones.length === 0 ? 'Sin secciones' : 'No hay resultados con los filtros aplicados'}
          </p>
          {selectedFilters.size > 0 && (
            <Button variant="link" onClick={clearFilters} className="mt-2 text-[#10B981]">Limpiar filtro</Button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map(s => {
            const count = countsBySeccion[s.numero] || 0;
            return (
              <Card
                key={s.id || s.numero}
                data-testid={`seccion-card-${s.numero}`}
                onClick={() => openDetail(s)}
                className="bg-white border border-[#E4E4E7] rounded-md hover:-translate-y-0.5 hover:shadow-md hover:border-[#10B981] transition-all duration-200 cursor-pointer group"
              >
                <CardContent className="p-5">
                  <div className="flex items-start justify-between">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="text-xl font-black text-[#0A0A0A] tracking-tighter font-mono" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>{s.numero}</p>
                        {count > 0 && (
                          <Badge className="bg-[#10B981]/10 text-[#10B981] border-0 text-xs font-bold">
                            <FileText className="w-3 h-3 mr-1" /> {count} gestion{count !== 1 ? 'es' : ''}
                          </Badge>
                        )}
                      </div>
                      <p className="text-sm text-[#52525B] mt-0.5 truncate">{s.nombre}</p>
                      <p className="text-xs text-[#52525B] mt-2 font-mono">{s.lat_center?.toFixed(4)}, {s.lng_center?.toFixed(4)}</p>
                      <p className="text-xs text-[#52525B]">Radio: {s.radio_km} km</p>
                    </div>
                    <div className="flex flex-col items-center gap-1 flex-shrink-0">
                      <ChevronRight className="w-5 h-5 text-[#A1A1AA] group-hover:text-[#10B981] group-hover:translate-x-1 transition-all" />
                      {isAdmin && (
                        <Button data-testid={`delete-seccion-${s.id || s.numero}`} variant="ghost" size="sm" onClick={(e) => handleDelete(s.id, e)} className="text-[#EF4444] hover:bg-[#EF4444]/10 h-8 w-8 p-0"><Trash2 className="w-4 h-4" /></Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <SeccionDetailDialog
        detail={detail}
        loading={loadingDetail}
        onClose={() => setDetail(null)}
      />
    </div>
  );
}

function FilterPanel({ secciones, selected, onToggle, onSelectAll, onClear }) {
  const [q, setQ] = useState('');
  const filtered = useMemo(() => {
    if (!q) return secciones;
    const s = q.toLowerCase();
    return secciones.filter(x =>
      x.numero?.toLowerCase().includes(s) ||
      x.nombre?.toLowerCase().includes(s)
    );
  }, [secciones, q]);
  return (
    <div className="flex flex-col max-h-96" data-testid="filter-panel">
      <div className="p-2 border-b border-[#E4E4E7]">
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#52525B]" />
          <Input
            data-testid="filter-search"
            className="h-9 pl-8 rounded-md text-sm"
            placeholder="Filtrar..."
            value={q}
            onChange={e => setQ(e.target.value)}
            autoFocus
          />
        </div>
        <div className="flex gap-1 mt-2">
          <Button size="sm" variant="ghost" onClick={onSelectAll} className="flex-1 h-7 text-xs">
            <Check className="w-3 h-3 mr-1" /> Todo
          </Button>
          <Button size="sm" variant="ghost" onClick={onClear} className="flex-1 h-7 text-xs">
            <X className="w-3 h-3 mr-1" /> Limpiar
          </Button>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto py-1">
        {filtered.length === 0 && <div className="text-xs text-[#52525B] text-center py-4">Sin resultados</div>}
        {filtered.map(s => {
          const active = selected.has(s.numero);
          return (
            <label
              key={s.numero}
              data-testid={`filter-option-${s.numero}`}
              className="flex items-center gap-2 px-3 py-2 hover:bg-[#F4F4F5] cursor-pointer text-sm"
            >
              <input
                type="checkbox"
                checked={active}
                onChange={() => onToggle(s.numero)}
                className="w-4 h-4 accent-[#10B981]"
              />
              <span className="font-mono font-bold text-[#0A0A0A]">{s.numero}</span>
              <span className="text-[#52525B] text-xs truncate flex-1">{s.nombre}</span>
            </label>
          );
        })}
      </div>
    </div>
  );
}

function SeccionDetailDialog({ detail, loading, onClose }) {
  const open = !!detail;
  const formatDate = (d) => d ? new Date(d).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' }) : '';

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="seccion-detail-dialog" className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col p-0">
        <DialogHeader className="p-6 pb-4 border-b border-[#E4E4E7]">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-md bg-[#0052FF]/10 flex items-center justify-center flex-shrink-0">
              <MapPin className="w-6 h-6 text-[#0052FF]" />
            </div>
            <div>
              <DialogTitle className="text-2xl font-black tracking-tight text-[#0A0A0A]" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
                Sección {detail?.numero}
              </DialogTitle>
              <DialogDescription className="text-sm text-[#52525B]">
                {detail?.nombre} · {detail?.total_gestiones || 0} gestiones · {detail?.total_vecinos || 0} vecinos
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto p-6 pt-4">
          {loading ? (
            <div className="flex items-center justify-center h-60">
              <div className="w-8 h-8 border-2 border-[#0052FF] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
            <Tabs defaultValue="gestiones">
              <TabsList className="grid w-full grid-cols-2 rounded-md mb-4">
                <TabsTrigger data-testid="tab-gestiones" value="gestiones">
                  <FileText className="w-4 h-4 mr-2" /> Gestiones ({detail?.total_gestiones || 0})
                </TabsTrigger>
                <TabsTrigger data-testid="tab-vecinos" value="vecinos">
                  <Users className="w-4 h-4 mr-2" /> Directorio ({detail?.total_vecinos || 0})
                </TabsTrigger>
              </TabsList>

              <TabsContent value="gestiones" className="space-y-2" data-testid="tab-gestiones-content">
                {(!detail?.gestiones || detail.gestiones.length === 0) ? (
                  <div className="text-center py-10">
                    <FileText className="w-10 h-10 text-[#E4E4E7] mx-auto mb-2" />
                    <p className="text-sm text-[#52525B]">Sin gestiones en esta sección</p>
                  </div>
                ) : detail.gestiones.map(g => (
                  <Link
                    key={g.id}
                    to={`/gestion/${g.id}`}
                    onClick={onClose}
                    data-testid={`seccion-gestion-${g.id}`}
                    className="block"
                  >
                    <div className="flex items-center gap-3 p-3 rounded-md border border-[#E4E4E7] hover:border-[#10B981] hover:shadow-sm hover:-translate-y-0.5 transition-all cursor-pointer group">
                      <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: STATUS_COLORS[g.status] }} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-bold text-[#0A0A0A] truncate">{g.tipo}</p>
                          <Badge variant="outline" className="text-[10px] flex-shrink-0" style={{ borderColor: STATUS_COLORS[g.status], color: STATUS_COLORS[g.status] }}>
                            {STATUS_LABELS[g.status]}
                          </Badge>
                        </div>
                        <p className="text-xs text-[#52525B] flex items-center gap-1.5 mt-0.5">
                          <User className="w-3 h-3" /> {g.vecino_nombre || 'Sin vecino'}
                          <span>·</span>
                          <Clock className="w-3 h-3" /> {formatDate(g.created_at)}
                          <span>·</span>
                          <span className="font-mono">#{g.id.slice(0, 8)}</span>
                        </p>
                      </div>
                      <ChevronRight className="w-4 h-4 text-[#A1A1AA] group-hover:text-[#10B981] group-hover:translate-x-1 transition-all" />
                    </div>
                  </Link>
                ))}
              </TabsContent>

              <TabsContent value="vecinos" className="space-y-2" data-testid="tab-vecinos-content">
                {(!detail?.vecinos || detail.vecinos.length === 0) ? (
                  <div className="text-center py-10">
                    <Users className="w-10 h-10 text-[#E4E4E7] mx-auto mb-2" />
                    <p className="text-sm text-[#52525B]">Sin vecinos registrados en esta sección</p>
                  </div>
                ) : detail.vecinos.map((v, idx) => {
                  const vecinoKey = v.telefono || `${v.nombre || ''}-${v.calle || ''}-${idx}`;
                  return (
                  <div key={vecinoKey} data-testid={`vecino-item-${idx}`} className="p-4 rounded-md border border-[#E4E4E7] hover:border-[#10B981] transition-colors">
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-md bg-[#0052FF]/10 flex items-center justify-center flex-shrink-0">
                            <User className="w-4 h-4 text-[#0052FF]" />
                          </div>
                          <p className="text-base font-bold text-[#0A0A0A] truncate">{v.nombre || 'Sin nombre'}</p>
                        </div>
                        <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 ml-10 text-sm text-[#52525B]">
                          {v.telefono && (
                            <a href={`https://wa.me/${v.telefono.replace(/\D/g, '')}`} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 hover:text-[#10B981]" onClick={e => e.stopPropagation()}>
                              <Phone className="w-3.5 h-3.5" /> {v.telefono}
                            </a>
                          )}
                          {(v.calle || v.colonia) && (
                            <span className="flex items-center gap-1.5">
                              <MapPin className="w-3.5 h-3.5" />
                              {[v.calle, v.numero, v.colonia].filter(Boolean).join(' ').trim() || 'Sin dirección'}
                            </span>
                          )}
                        </div>
                      </div>
                      <Badge className="bg-[#10B981]/10 text-[#10B981] border-0 font-bold">
                        {v.gestiones.length} gestion{v.gestiones.length !== 1 ? 'es' : ''}
                      </Badge>
                    </div>
                    {v.gestiones.length > 0 && (
                      <div className="mt-3 ml-10 flex flex-wrap gap-1.5">
                        {v.gestiones.map(g => (
                          <Link
                            key={g.id}
                            to={`/gestion/${g.id}`}
                            onClick={onClose}
                            data-testid={`vecino-gestion-${g.id}`}
                            className="text-[11px] px-2 py-1 rounded-full border border-[#E4E4E7] hover:border-[#10B981] hover:bg-[#10B981]/5 transition-colors flex items-center gap-1 font-medium"
                            style={{ color: STATUS_COLORS[g.status] }}
                          >
                            <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: STATUS_COLORS[g.status] }} />
                            {g.tipo} · #{g.id.slice(0, 6)}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                  );
                })}
              </TabsContent>
            </Tabs>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
